import {
  HeadContent,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import appCss from "../styles.css?url";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ColorThemeProvider } from "@/components/providers/color-theme-provider";
import { LenisProvider } from "@/components/providers/lenis-provider";
import { ThemeProvider as NewThemeProvider } from "@/components/providers/theme-provider";
import { AuthProvider } from "@/components/auth/AuthProvider";
import ReferralRedirect from "@/components/ReferralRedirect";
import MobileBottomCTA from "@/components/MobileBottomCTA";
// import GiveawayPopup from "@/components/GiveawayPopup";
import { initPostHog } from "@/lib/posthog";
import { trackMetaPageView } from "@/lib/meta-pixel";
import { LanguageProvider } from "@/components/LanguageProvider";

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const ALLOWED_META_TRACKING_HOSTS = new Set(["ai.zapify.pro", "zapify.pro"]);
const DEFAULT_META_TITLE =
  "Zapify | Instagram Approved Comment & DM Automation Platform";
const DEFAULT_META_DESCRIPTION =
  "Turn Instagram engagement into leads & followers with automated DMs, comment replies, and AI-powered follow-ups. Zapify helps brands, creators, and agencies respond faster and scale conversations.";

type MetaQueueFbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: (...args: unknown[]) => number;
};

function isAllowedMetaTrackingHost(hostname: string): boolean {
  return ALLOWED_META_TRACKING_HOSTS.has(hostname);
}

export const Route = createRootRoute({
  head: () => ({
    title: DEFAULT_META_TITLE,
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        name: "description",
        content: DEFAULT_META_DESCRIPTION,
      },
      {
        name: "keywords",
        content:
          "AI, social media, Instagram, engagement, automation, comment management, content creators, voice, personalized replies",
      },
      {
        name: "theme-color",
        content: "#DDA5C4",
      },
      // Open Graph
      {
        property: "og:title",
        content: DEFAULT_META_TITLE,
      },
      {
        property: "og:description",
        content: DEFAULT_META_DESCRIPTION,
      },
      {
        property: "og:url",
        content: "https://zapify.pro",
      },
      {
        property: "og:site_name",
        content: "Zapify",
      },
      {
        property: "og:image",
        content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        property: "og:image:alt",
        content: DEFAULT_META_TITLE,
      },
      {
        property: "og:locale",
        content: "en_US",
      },
      {
        property: "og:type",
        content: "website",
      },
      // Twitter Card
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:site",
        content: "@ZapifyPro",
      },
      {
        name: "twitter:title",
        content: DEFAULT_META_TITLE,
      },
      {
        name: "twitter:description",
        content: DEFAULT_META_DESCRIPTION,
      },
      {
        name: "twitter:image",
        content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
      },
    ],
    links: [
      // Preload CSS for faster LCP
      {
        rel: "preload",
        href: appCss,
        as: "style",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Favicon
      {
        rel: "icon",
        href: "/favicon.ico",
        type: "image/x-icon",
      },
      {
        rel: "apple-touch-icon",
        href: "/logo-small.png",
      },
      // DNS prefetch for third-party domains
      {
        rel: "dns-prefetch",
        href: "https://analytics.ahrefs.com",
      },
      {
        rel: "dns-prefetch",
        href: "https://connect.facebook.net",
      },
      {
        rel: "dns-prefetch",
        href: "https://www.googletagmanager.com",
      },
      {
        rel: "dns-prefetch",
        href: "https://us-assets.i.posthog.com",
      },
    ],
  }),

  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isStudioPath = pathname === "/studio" || pathname.startsWith("/studio/");

  // Lazy-initialize PostHog after page is interactive
  useEffect(() => {
    if (isStudioPath) return;

    const init = () => initPostHog();
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(init);
    } else {
      setTimeout(init, 1);
    }
  }, [isStudioPath]);

  // Load third-party analytics after hydration so we don't mutate the
  // document tree before React hydrates it. Meta still gets an immediate
  // queue stub + init so PageView can enqueue reliably on first load.
  useEffect(() => {
    if (isStudioPath) return;

    const requestIdle =
      "requestIdleCallback" in window
        ? (window.requestIdleCallback as (callback: () => void) => number)
        : (callback: () => void) => window.setTimeout(callback, 1);

    const loadScript = (
      src: string,
      {
        target = document.head,
        attributes = {},
      }: {
        target?: HTMLElement;
        attributes?: Record<string, string>;
      } = {},
    ) => {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const script = document.createElement("script");
      script.async = true;
      script.src = src;

      for (const [name, value] of Object.entries(attributes)) {
        script.setAttribute(name, value);
      }

      target.appendChild(script);
    };

    let isLoaded = false;
    const loadAnalytics = () => {
      if (isLoaded) return;
      isLoaded = true;

      loadScript("https://www.googletagmanager.com/gtag/js?id=G-H4JRCDRJEL");

      const analyticsWindow = window as Window & {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        __FB_PIXEL_INITIALIZED__?: boolean;
        _fbq?: typeof window.fbq;
      };

      analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
      analyticsWindow.gtag =
        analyticsWindow.gtag ||
        function gtag(...args: unknown[]) {
          analyticsWindow.dataLayer?.push(args);
        };

      analyticsWindow.gtag("js", new Date());
      analyticsWindow.gtag("config", "G-H4JRCDRJEL");

      if (
        META_PIXEL_ID &&
        isAllowedMetaTrackingHost(window.location.hostname) &&
        !analyticsWindow.__FB_PIXEL_INITIALIZED__
      ) {
        analyticsWindow.__FB_PIXEL_INITIALIZED__ = true;

        if (!window.fbq) {
          const fbqStub = ((...args: unknown[]) => {
            if (fbqStub.callMethod) {
              fbqStub.callMethod(...args);
              return;
            }

            fbqStub.queue.push(args);
          }) as MetaQueueFbq;

          fbqStub.queue = [];
          fbqStub.loaded = true;
          fbqStub.version = "2.0";
          fbqStub.push = (...args: unknown[]) => fbqStub.queue.push(args);

          window.fbq = fbqStub;
          analyticsWindow._fbq = fbqStub;
        }

        window.fbq("init", META_PIXEL_ID);
        loadScript("https://connect.facebook.net/en_US/fbevents.js");
      }

      // loadScript("https://analytics.ahrefs.com/analytics.js", {
      //   target: document.body,
      //   attributes: { defer: "true", "data-key": "uFkvPKBMEneiFirZsxnKVw" },
      // });
    };

    const onFirstInput = () => {
      loadAnalytics();
    };

    window.addEventListener("pointerdown", onFirstInput, { passive: true, once: true });
    window.addEventListener("keydown", onFirstInput, { passive: true, once: true });
    window.addEventListener("touchstart", onFirstInput, { passive: true, once: true });

    // Fallback: still load eventually, but keep initial CPU path cleaner for LCP/TBT.
    const delayedLoad = window.setTimeout(() => {
      requestIdle(loadAnalytics);
    }, 4000);

    return () => {
      window.removeEventListener("pointerdown", onFirstInput);
      window.removeEventListener("keydown", onFirstInput);
      window.removeEventListener("touchstart", onFirstInput);
      window.clearTimeout(delayedLoad);
    };
  }, [isStudioPath]);
  // Fire a deduplicated Meta PageView (browser pixel + Conversions API) on
  // every route change — RootDocument doesn't unmount between SPA navigations,
  // so a single mount-effect would only count the first view. Keying on the
  // current pathname re-runs for each client-side navigation.
  useEffect(() => {
    if (!META_PIXEL_ID || isStudioPath) return;
    if (!isAllowedMetaTrackingHost(window.location.hostname)) return;
    const fire = () => {
      void trackMetaPageView();
    };
    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(fire);
    } else {
      setTimeout(fire, 1);
    }
  }, [pathname, isStudioPath]);

  return (
    <html lang="en" suppressHydrationWarning className="h-full antialiased">
      <head>
        <HeadContent />
        {/* CRITICAL: Redirect script - MUST be first to prevent page flash */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                try {
                  var params = new URLSearchParams(window.location.search);

                  // Handle redirect parameter immediately (higher priority)
                  if (params.has("redirect")) {
                    var redirectUrl = params.get("redirect");
                    if (redirectUrl) {
                      var decodedUrl = decodeURIComponent(redirectUrl);
                      // Instant redirect before page renders
                      window.location.href = decodedUrl;
                    }
                  }
                } catch (err) {
                  // ignore
                }
              })();
            `,
          }}
        />
        {/* Blocking script to prevent theme flash */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                try {
                  // Handle dark/light theme
                  var stored = localStorage.getItem('zapify-theme-mode');
                  var initialTheme = stored === 'dark' ? 'dark' : 'light';

                  if (initialTheme === 'dark') {
                    document.documentElement.classList.add('dark');
                  } else {
                    document.documentElement.classList.remove('dark');
                  }

                  // Handle accent color theme (prevents color flicker)
                  var themeColor = localStorage.getItem('zapify-theme-color');
                  var LEGACY_ACCENT = { violet: 'blue', cyan: 'blue', rose: 'red' };
                  var validColors = ['zapify', 'blue', 'orange', 'green', 'yellow', 'red'];

                  if (themeColor) {
                    // Handle legacy color names
                    if (LEGACY_ACCENT[themeColor]) {
                      themeColor = LEGACY_ACCENT[themeColor];
                    }
                    // Validate and apply
                    if (validColors.indexOf(themeColor) !== -1) {
                      document.documentElement.dataset.themeColor = themeColor;
                    } else {
                      document.documentElement.dataset.themeColor = 'yellow';
                    }
                  } else {
                    document.documentElement.dataset.themeColor = 'yellow';
                  }
                } catch (e) {}
              })();
            `,
          }}
        />
      </head>
      <body className={isStudioPath ? "antialiased" : "antialiased overflow-x-hidden"}>
        {isStudioPath ? (
          <>
            {children}
            <Scripts />
          </>
        ) : (
          <>
            <ColorThemeProvider>
              <ThemeProvider>
                <NewThemeProvider>
                  <LenisProvider>
                    <LanguageProvider>
                      <AuthProvider>
                        <div className="w-full min-h-screen overflow-x-hidden">
                          <Navbar />
                          <div className="relative z-10 flex flex-col w-full mx-auto overflow-hidden">
                            {children}
                          </div>
                          <Footer />
                          <MobileBottomCTA />
                        </div>
                      </AuthProvider>
                    </LanguageProvider>
                  </LenisProvider>
                </NewThemeProvider>
              </ThemeProvider>
            </ColorThemeProvider>
            <ReferralRedirect />
            {/* <GiveawayPopup /> */}
            <Scripts />
          </>
        )}
      </body>
    </html>
  );
}
